import type { EventsPage } from '@finder/contracts';
import type { LoadErrorKind } from '../api-client.js';
import { MAX_DISTANCE_MILES, nextDistancePreset } from '../filters/Filters.js';
import { Icon } from '../results/Icon.js';
import { formatSourcesLine, formatUpdatedAgo, isKnownPromotion } from '../trust/presentation.js';

const LOAD_ERROR_COPY: Record<LoadErrorKind, { title: string; body: string }> = {
  offline: {
    title: 'You’re offline',
    body: 'Connect to the internet, then try again. Events can’t load without a network.',
  },
  server: {
    title: 'The events service had a problem',
    body: 'We reached the service, but it couldn’t return events. Try again in a moment.',
  },
};

/**
 * Result-state surfaces (UI-REDESIGN.md §6, issue #13). Each state has a visual
 * identity instead of a bare line, and every accessibility role from the prior
 * implementation is preserved (status / alert / status).
 */

/** Loading: 3 skeleton cards. Shimmer is CSS-only and suppressed under
 *  prefers-reduced-motion. The screen-reader text keeps role="status". */
export function LoadingState(): JSX.Element {
  return (
    <div className="skeletons" aria-hidden="false">
      <p className="visually-hidden" role="status" aria-live="polite">
        Finding events with free food & drinks near you…
      </p>
      {[0, 1, 2].map((i) => (
        <div key={i} className="skeleton-card" aria-hidden="true">
          <div className="sk sk--timing" />
          <div className="sk sk--title" />
          <div className="sk sk--row" />
          <div className="sk sk--pills" />
          <div className="sk sk--cta" />
        </div>
      ))}
    </div>
  );
}

export function ErrorState({
  kind,
  onRetry,
}: {
  kind: LoadErrorKind;
  onRetry: () => void;
}): JSX.Element {
  const copy = LOAD_ERROR_COPY[kind];
  return (
    <div className="state-card state-card--error" role="alert">
      <Icon name="search-x" size={40} />
      <h3>{copy.title}</h3>
      <p>{copy.body}</p>
      <button type="button" className="btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** Next couple of days. A shorter window offers this before a wider radius. */
const NEXT_COUPLE_DAYS = 3;
/** 2 weeks. Check back later only at this window, with nothing inside 25 mi. */
const TWO_WEEKS = 14;

/**
 * Empty result (issue #69). Today offers the next couple of days. That
 * couple-of-days window offers the next 2 weeks before a wider radius.
 * Widen only when the API's nearest-outside fact is an event inside this
 * window. Check back later only after 2 weeks with nothing inside 25 miles.
 * `onReturnToNarrow` is the way back after an automatic wider search is also empty.
 */
export function EmptyState({
  page,
  windowDays,
  onWiden,
  onShowNextCoupleDays,
  onShowTwoWeeks,
  onReturnToNarrow,
  returnLabel,
}: {
  page: EventsPage;
  windowDays: number;
  onWiden?: (miles: number) => void;
  onShowNextCoupleDays?: () => void;
  onShowTwoWeeks?: () => void;
  onReturnToNarrow?: () => void;
  returnLabel?: string;
}): JSX.Element {
  const nearest = page.nearestOutsideRadiusMiles;
  // Snap to a real distance chip. ceil(8.2) → 9 matched no chip, and a radius
  // of 25 still cannot include an event that is farther than 25.
  const widenTo = nearest !== undefined ? nextDistancePreset(nearest) : null;
  const shortWindow = windowDays < NEXT_COUPLE_DAYS;
  const coupleWindow = windowDays === NEXT_COUPLE_DAYS;
  const canWiden = widenTo !== null && onWiden !== undefined;
  const nothingInsideCap = nearest === undefined || widenTo === null;
  const checkBackLater = windowDays >= TWO_WEEKS && nothingInsideCap;
  const windowBeforeRadius = shortWindow || (coupleWindow && onShowTwoWeeks !== undefined);
  const nearestSentence =
    !shortWindow && canWiden ? (
      <p>
        The nearest event with free food & drinks is about {nearest!.toFixed(1)} miles away — try
        widening your search.
      </p>
    ) : checkBackLater ? (
      <p>Check back later — new events with free food & drinks are added twice a day.</p>
    ) : !shortWindow && nearest !== undefined && widenTo === null ? (
      <p>
        The nearest event with free food & drinks is about {nearest.toFixed(1)} miles away, past the{' '}
        {MAX_DISTANCE_MILES} mile search.
      </p>
    ) : null;
  return (
    <div className="state-card state-card--empty" role="status">
      <Icon name="search-x" size={48} />
      {shortWindow ? (
        <h3>Nothing with free food today.</h3>
      ) : coupleWindow ? (
        <h3>Nothing with free food in the next couple of days.</h3>
      ) : (
        <h3>No events with free food & drinks match right now</h3>
      )}
      {nearestSentence}
      <div className="state-card__actions">
        {shortWindow && onShowNextCoupleDays ? (
          <button type="button" className="btn" onClick={onShowNextCoupleDays}>
            Show the next couple of days
          </button>
        ) : null}
        {coupleWindow && onShowTwoWeeks ? (
          <button type="button" className="btn" onClick={onShowTwoWeeks}>
            Show the next 2 weeks
          </button>
        ) : null}
        {canWiden ? (
          <button
            type="button"
            className={windowBeforeRadius ? 'btn btn-ghost' : 'btn'}
            onClick={() => onWiden(widenTo)}
          >
            Widen to {widenTo} mi
            <Icon name="arrow" size={16} />
          </button>
        ) : null}
        {onReturnToNarrow && returnLabel ? (
          <button type="button" className="btn" onClick={onReturnToNarrow}>
            {returnLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The default couple-of-days search was empty, so the list is a wider window.
 * The button returns to that narrower search and leaves it empty if it still is.
 */
export function WiderWindowNotice({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel: string;
  onAction: () => void;
}): JSX.Element {
  return (
    <div className="fallback-banner" role="status">
      <p>{message}</p>
      <button type="button" className="btn btn-ghost" onClick={onAction}>
        {actionLabel}
      </button>
    </div>
  );
}

/**
 * The API returned events, and the client-side filters removed all of them.
 * Distance and the date window stay; those have their own server empty state.
 */
export function FilteredEmptyState({ onClear }: { onClear?: () => void }): JSX.Element {
  return (
    <div className="state-card state-card--empty" role="status">
      <Icon name="search-x" size={48} />
      <h3>No events match these filters</h3>
      <p>Distance and dates stay as they are. Clear the extra filters to see the list again.</p>
      {onClear ? (
        <button type="button" className="btn" onClick={onClear}>
          Clear extra filters
        </button>
      ) : null}
    </div>
  );
}

/**
 * Always-visible freshness (issue #10). Shown whenever a page is on screen,
 * including when the generation is fresh — StaleNotice stays the warning that
 * appears only once `isStale` is true. No motion; the clock icon is decorative.
 */
export function FreshnessIndicator({
  updatedAt,
  now = Date.now(),
}: {
  updatedAt: string;
  now?: number;
}): JSX.Element {
  const label = formatUpdatedAgo(updatedAt, now);
  const known = isKnownPromotion(updatedAt);
  const absolute = known
    ? new Date(updatedAt).toLocaleString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
  const relative = label === 'Updated just now' || label.endsWith(' ago');
  return (
    <p className="freshness">
      <Icon name="clock" size={15} />
      {known ? (
        <time dateTime={new Date(updatedAt).toISOString()} title={absolute}>
          {label}
          {relative ? <span className="visually-hidden"> ({absolute} UTC)</span> : null}
        </time>
      ) : (
        <span>{label}</span>
      )}
    </p>
  );
}

/**
 * Short provenance line for the header. Before a page arrives, empty `names`
 * still names the known calendars. A loaded page with no events keeps one
 * stable line and does not swap in that longer list.
 */
export function SourcesLine({
  names,
  settled = false,
}: {
  names: readonly string[];
  settled?: boolean;
}): JSX.Element {
  const text =
    settled && names.length === 0 ? 'Sourced from public calendars.' : formatSourcesLine(names);
  return <p className="sources-line">{text}</p>;
}

/** Non-blocking staleness notice, restyled as a soft amber banner. */
export function StaleNotice({ page }: { page: EventsPage }): JSX.Element | null {
  if (!page.isStale) return null;
  const when = new Date(page.lastSuccessfulPromotionAt).toLocaleString('en-US');
  return (
    <p className="stale-banner" role="status">
      <Icon name="clock" size={15} />
      Showing the latest available list (updated {when}). A refresh may be in progress.
    </p>
  );
}
